import { mapValues } from "lodash";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { findAndSetColumnSortOrder, StateChangeCell, TextCell } from "@similarweb/pro-logic-components";

import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { DrilldownWrapper } from "app/components/keyword-research/landing-pages/styledComponents";
import {
    StyledEnrichButton,
    StyledEnrichButtonNumber,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/StyledComponents";
import { allTrackers } from "app/services/track/track";
import {
    GroupTrafficShare,
    TrafficShareWithVisits,
    UrlCellWebsiteAnalysis,
    WaKeywordPositionCompare,
} from "app/components/React/Table/cells";
import { CompareBarCell } from "app/components/React/Table/cells/CompareBarCell";
import type { GroupTrafficShareProps } from "app/components/React/Table/cells/GroupTrafficShare";
import { IntentCell } from "app/components/React/Table/cells/IntentCell";
import type { KeywordDifficultyCellProps } from "app/components/React/Table/cells/KeywordDifficultyCell";
import { KeywordDifficultyCell } from "app/components/React/Table/cells/KeywordDifficultyCell";
import { SearchKeywordCellWithAutoCreate } from "app/components/React/Table/cells/SearchKeywordCellWithAutoCreate";
import type { tooltipChildType } from "app/components/React/Table/cells/WaKeywordPositionCompare";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { capitalizeFirstLetterFilter, i18nTemplateFilter, numberFilter } from "app/filters/ngFilters";
import { UNDEFINED_SIGN } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import { SerpTableCellCompare } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellCompare";
import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";
import type { dateForTooltip } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import type { KeywordTransformedData } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import {
    TABLE_COLUMNS_PREFERENCES_KEY,
    filterAndSortColumnsForTable,
    getCustomContainerForTopUrlsTooltip,
} from "../WebsiteKeywordsPageUtillities2";

import { formatPositionChange } from "./changeValueFormatters";

const positionCompareCellTooltipChild: tooltipChildType = "cell";

const getShareColumns = (isComparedDuration: boolean) => {
    const trafficShareRender = ({ totalClicks, value }) => (
        <TrafficShareWithVisits
            row={{ totalClicks }}
            value={value}
            applyAbbrNumberFilter
            undefinedSign={UNDEFINED_SIGN}
            totalVisitsRowProp="totalClicks"
            thresholdForTotalFilter={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
            thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
        />
    );

    const shareColumnTitle = translate(
        isComparedDuration
            ? "keywords.by.website.table.columns.clicks.compareDuration.title"
            : "keywords.by.website.table.columns.clicks.title",
    );
    const shareColumnTooltipSubtitle = translate(
        isComparedDuration
            ? "keywords.by.website.table.columns.clicks.compareDuration.tooltip"
            : "website.keywords.2.0.table.clicks.column.tooltip",
    );

    const shareColumn = {
        dataIndex: "share",
        title: shareColumnTitle,
        tooltipTitle: shareColumnTitle,
        tooltipSubtitle: shareColumnTooltipSubtitle,
        render: trafficShareRender,
        width: 150,
        sorter: true,
    };

    const previousClicksPopColumn = {
        dataIndex: "previousClicksPop",
        title: translate("keywords.by.website.table.columns.previous.clicks.title"),
        tooltipTitle: translate("keywords.by.website.table.columns.previous.clicks.title"),
        tooltipSubtitle: translate("website.keywords.2.0.table.previous.clicks.column.tooltip"),
        render: trafficShareRender,
        width: 150,
        sorter: true,
    };

    if (isComparedDuration) {
        return {
            dataIndex: "share",
            align: "center",
            title: translate("keywords.by.website.table.columns.clicks.pop.title"),
            tooltipTitle: translate("keywords.by.website.table.columns.clicks.pop.title"),
            tooltipSubtitle: translate("website.keywords.2.0.table.previous.clicks.column.tooltip"),
            width: 300,
            children: [shareColumn, previousClicksPopColumn],
        };
    }

    return shareColumn;
};

const onKeywordClick = (value: string) => {
    allTrackers.dispatchTrackEvent("Internal Link", "click", `table/${value}`);
};
const GroupTrafficShareWithoutPercentage = (
    props: Record<string, unknown> & {
        row: { siteClicks: GroupTrafficShareProps["valueForTooltip"] };
        value: GroupTrafficShareProps["value"];
    },
) => {
    const updatedProps = { ...props, valueForTooltip: props?.row?.siteClicks } as GroupTrafficShareProps;
    return <GroupTrafficShare {...updatedProps} />;
};

export interface IColumnsConfigArguments {
    country: number;
    webSource: string;
    domains: string[];
    sourceType: keyof typeof EKeywordTabs;
    sortConfig: { sort: string; asc: boolean };
    duration: string;
    selectedPageTab: keyof typeof EKeywordTabs;
    comparedDuration: string;
    currentPage: number;
    pageSize: number;
    toggleExpandRow?: (key: string) => void;
    totalCount: number;
}

export const getColumnsConfig = ({
    country,
    webSource,
    domains,
    sourceType,
    sortConfig,
    duration,
    selectedPageTab,
    comparedDuration,
    currentPage,
    pageSize,
    toggleExpandRow,
    totalCount = 0,
}: IColumnsConfigArguments): ColumnsType<KeywordTransformedData> => {
    const savedStateColumns = PreferencesService.get(TABLE_COLUMNS_PREFERENCES_KEY);
    const isTotalTabSelected = selectedPageTab === EKeywordTabs[EKeywordTabs.Total];
    const showDifficulty = sourceType !== EKeywordTabs[EKeywordTabs.Paid];
    const showCompetition = sourceType !== EKeywordTabs[EKeywordTabs.Organic] && country !== 999;
    const isTotal = webSource === devicesTypes.TOTAL;
    const compare = domains.length > 1;
    const durationObject = DurationService.getDurationData(duration);
    const durationTooltip = mapValues(durationObject.forTooltip as dateForTooltip, function (v) {
        return decodeURIComponent(v);
    });
    const isWindow = durationObject.forAPI.isWindow;
    const durationTooltipParams = {
        ["currentMonth"]: durationTooltip.to,
        ["lastMonth"]: durationTooltip.from,
    };
    const fixedColumns = [
        {
            ...getIndexCellPropsForTableV2(currentPage, pageSize),
        },
        {
            dataIndex: "keyword",
            title: `${translate("keywords.by.website.table.columns.keyword.title")} (${numberFilter()(totalCount)})`,
            tooltipTitle: translate("keywords.by.website.table.columns.keyword.title"),
            tooltipSubtitle: translate("website.keywords.2.0.table.keyword.column.tooltip"),
            fixed: "left",
            width: 184,
            render: (rowCellData, row) => (
                <SearchKeywordCellWithAutoCreate
                    {...row}
                    value={rowCellData as string}
                    tooltipContent={translate("keywords.by.website.table.columns.keyword.tooltip")}
                    onItemClick={onKeywordClick}
                    destinationState={"organicsearch_keywordAnalysis_2"}
                    destinationStateParams={{
                        selectedPageTab: capitalizeFirstLetterFilter()(sourceType),
                    }}
                />
            ),
        },
    ];

    const columns = [
        getShareColumns(!!comparedDuration),
        {
            dataIndex: "clicksChange",
            title: translate("analysis.source.search.all.table.columns.change.title"),
            tooltipTitle: translate("analysis.source.search.all.table.columns.change.title"),
            tooltipSubtitle: i18nTemplateFilter()(
                "website.keywords.2.0.table.change.column.tooltip",
                durationTooltipParams,
            ),
            sorter: true,
            width: 90,
            align: "right",
            render: (rowCellData) => {
                return (
                    <StateChangeCell
                        value={rowCellData}
                        formattedValue={rowCellData.changeValueFormatted}
                    />
                );
            },
        },
        isTotalTabSelected
            ? {
                  dataIndex: "organicClicksShare",
                  title: translate("mobile.v2.table.columns.sourceType.share.title"),
                  tooltipTitle: translate("mobile.v2.table.columns.sourceType.share.title"),
                  tooltipSubtitle: translate("website.keywords.2.0.table.sourceType.column.tooltip"),
                  width: 114,
                  render: (rowCellData, row) => (
                      <CompareBarCell
                          value={rowCellData}
                          isSourceType={true}
                          displayNoData={row.Clicks < 1}
                          {...rowCellData}
                      />
                  ),
              }
            : null,

        showDifficulty
            ? {
                  dataIndex: "difficulty",
                  title: translate("analysis.source.search.all.table.columns.keyword.difficulty.title"),
                  tooltipTitle: translate("analysis.source.search.all.table.columns.keyword.difficulty.title"),
                  tooltipSubtitle: translate("analysis.source.search.all.table.columns.keyword.difficulty.tooltip", {
                      date: durationObject.raw.to.format("MMM YYYY"),
                  }),
                  width: 62,
                  align: "left",
                  render: (rowCellData, row) => {
                      const props: KeywordDifficultyCellProps = {
                          value: rowCellData as KeywordDifficultyCellProps["value"],
                          row: row as KeywordDifficultyCellProps["row"],
                      };
                      return <KeywordDifficultyCell {...props} />;
                  },
              }
            : null,

        showCompetition
            ? {
                  dataIndex: "competition",
                  title: translate("analysis.source.search.all.table.columns.keyword.competition.title"),
                  tooltipTitle: translate("analysis.source.search.all.table.columns.keyword.competition.title"),
                  tooltipSubtitle: translate("analysis.source.search.all.table.columns.keyword.competition.tooltip"),
                  width: 62,
                  render: (rowCellData, row) => {
                      const props: KeywordDifficultyCellProps = {
                          value: rowCellData as KeywordDifficultyCellProps["value"],
                          row: row as KeywordDifficultyCellProps["row"],
                      };
                      return <KeywordDifficultyCell {...props} />;
                  },
              }
            : null,
        {
            dataIndex: "intent",
            title: translate("keywords.table.header.intent.display.name"),
            tooltipTitle: translate("keywords.table.header.intent.display.name"),
            tooltipSubtitle: translate("keywords.table.header.intent.tooltip"),
            width: 100,
            align: "left",
            render: (rowCellData, row) => (
                <IntentCell
                    value={rowCellData}
                    row={{ ...row, SearchTerm: row.keyword }}
                />
            ),
        },
        compare
            ? {
                  dataIndex: "siteOrigins",
                  title: translate("analysis.source.search.all.table.columns.shareCompare.title"),
                  tooltipTitle: translate("analysis.source.search.all.table.columns.shareCompare.title"),
                  tooltipSubtitle: translate("website.keywords.2.0.table.shareCompare.column.tooltip"),
                  render: (rowCellData, row) => (
                      <GroupTrafficShareWithoutPercentage
                          value={rowCellData}
                          row={row}
                      />
                  ),
                  width: 162,
              }
            : null,
        ...(!isWindow
            ? [
                  {
                      dataIndex: "kwVolume",
                      title: translate("website.keywords.2.0.table.monthly.volume.column"),
                      tooltipTitle: translate("website.keywords.2.0.table.monthly.volume.column"),
                      tooltipSubtitle: translate("website.keywords.2.0.table.monthly.volume.column.tooltip"),
                      render: (rowCellData) => <TextCell fontVariant="input2">{rowCellData}</TextCell>,
                      sorter: true,
                      width: 90,
                      align: "right",
                  },
              ]
            : [
                  {
                      dataIndex: "kwWindowVolume",
                      title: translate("website.keywords.2.0.table.window.volume.column"),
                      tooltipTitle: translate("website.keywords.2.0.table.window.volume.column"),
                      tooltipSubtitle: translate("website.keywords.2.0.table.window.volume.column.tooltip"),
                      render: (rowCellData) => <TextCell fontVariant="input2">{rowCellData}</TextCell>,
                      sorter: true,
                      width: 94,
                      align: "right",
                  },
                  {
                      dataIndex: "volumeChange",
                      title: translate("website.keywords.2.0.table.window.volume.change.column"),
                      tooltipTitle: translate("website.keywords.2.0.table.window.volume.change.column"),
                      tooltipSubtitle: translate("website.keywords.2.0.table.volume.change.column.tooltip"),
                      render: (rowCellData) => {
                          return (
                              <StateChangeCell
                                  value={rowCellData}
                                  formattedValue={rowCellData.changeValueFormatted}
                              />
                          );
                      },
                      width: 104,
                      align: "right",
                  },
              ]),
        {
            dataIndex: "kwVolumeAverage",
            title: translate("analysis.source.search.all.table.columns.average.volume.title"),
            tooltipTitle: translate("analysis.source.search.all.table.columns.average.volume.title"),
            tooltipSubtitle: translate("website.keywords.2.0.table.average.volume.column.tooltip"),
            render: (rowCellData) => <TextCell fontVariant="input2">{rowCellData}</TextCell>,
            sorter: false,
            width: 92,
            align: "right",
        },
        isTotal
            ? {
                  dataIndex: "webSourceShare",
                  title: translate("mobile.v2.table.columns.websource.share.title"),
                  tooltipTitle: translate("mobile.v2.table.columns.websource.share.title"),
                  tooltipSubtitle: translate("website.keywords.2.0.table.websource.column.tooltip"),
                  width: 124,
                  render: (rowCellData, row) => (
                      <CompareBarCell
                          value={rowCellData}
                          displayNoData={row.Clicks < 1}
                          {...rowCellData}
                      />
                  ),
              }
            : null,
        {
            dataIndex: "cpc",
            title: translate("analysis.source.search.all.table.columns.cpc.title"),
            tooltipTitle: translate("analysis.source.search.all.table.columns.cpc.title"),
            tooltipSubtitle: translate("website.keywords.2.0.table.cpc.column.tooltip"),
            render: (rowCellData) => <TextCell fontVariant="input2">{rowCellData}</TextCell>,
            width: 62,
            align: "right",
        },
        {
            dataIndex: "zeroClicksShare",
            title: translate("digitalsuite.website.keywords.table.zero.clicks.title"),
            tooltipTitle: translate("digitalsuite.website.keywords.table.zero.clicks.title"),
            tooltipSubtitle: translate("website.keywords.2.0.table.zero.clicks.column.tooltip"),
            render: (rowCellData) => <TextCell fontVariant="input2">{rowCellData}</TextCell>,
            align: "right",
            width: 84,
        },
        ...(country !== 999 && sourceType !== EKeywordTabs[EKeywordTabs.Paid]
            ? [
                  {
                      dataIndex: "position",
                      title: translate("website.keywords.2.0.page.table.position.column.title"),
                      tooltipTitle: translate("website.keywords.2.0.page.table.position.column.title"),
                      tooltipSubtitle: translate(
                          `website.keywords.2.0.table.column.tooltip.position${sourceType?.toLowerCase()}.title`,
                      ),
                      format: "minusNAFilter",
                      sorter: true,
                      width: 58,
                      render: (rowCellData: { Key: string; Value: number }[], row) => {
                          const props = {
                              field: "position",
                              row: { position: rowCellData },
                              ...row,
                          };
                          return compare ? (
                              <WaKeywordPositionCompare
                                  tooltipChild={positionCompareCellTooltipChild}
                                  {...props}
                              />
                          ) : (
                              <TextCell fontVariant="input2">
                                  {rowCellData[0].Value === -1 ? UNDEFINED_SIGN : rowCellData[0].Value}
                              </TextCell>
                          );
                      },
                  },
                  ...(!compare
                      ? [
                            {
                                dataIndex: "sitesData",
                                title: translate("keywords.by.website.table.columns.pos.change.title"),
                                tooltipTitle: translate("keywords.by.website.table.columns.pos.change.title"),
                                tooltipSubtitle: translate("website.keywords.2.0.table.pos.change.column.tooltip"),
                                width: 68,
                                align: "right",
                                render: (rowCellData) => {
                                    const { Position, PreviousPosition } = rowCellData?.[0] ?? {};
                                    const { value, formattedValue } = formatPositionChange(Position, PreviousPosition);
                                    return (
                                        <StateChangeCell
                                            value={value}
                                            formattedValue={formattedValue}
                                            noChangeSign={"0"}
                                        />
                                    );
                                },
                            },
                        ]
                      : []),
              ]
            : []),
        compare
            ? {
                  dataIndex: "topUrls",
                  title: translate("keywords.by.website.table.columns.top.url.title"),
                  tooltipTitle: translate("keywords.by.website.table.columns.top.url.title"),
                  tooltipSubtitle: `website.keywords.2.0.table.column.tooltip.destination${sourceType?.toLowerCase()}.title`,
                  width: 210,
                  render: (rowCellData, row) => (
                      <UrlCellWebsiteAnalysis
                          value={rowCellData}
                          customContainer={getCustomContainerForTopUrlsTooltip(rowCellData, row.keyword)}
                      />
                  ),
                  align: "left",
              }
            : {
                  dataIndex: "topUrl",
                  title: translate("keywords.by.website.table.columns.top.url.title"),
                  tooltipTitle: translate("keywords.by.website.table.columns.top.url.title"),
                  tooltipSubtitle: `website.keywords.2.0.table.column.tooltip.destination${sourceType?.toLowerCase()}.title`,
                  width: 210,
                  render: (rowCellData) => <UrlCellWebsiteAnalysis value={rowCellData} />,
                  align: "left",
              },

        !comparedDuration
            ? {
                  dataIndex: "urlCount",
                  title: translate("keywords.by.website.table.columns.url.count.title"),
                  tooltipTitle: translate("keywords.by.website.table.columns.url.count.title"),
                  tooltipSubtitle: `keywords.by.website.table.columns.url.count.title.tooltip`,
                  onCell: (record) => ({
                      onClick: () => {
                          if (record["urlCount"] > 0) {
                              toggleExpandRow(record.key as string);
                          }
                      },
                  }),
                  render: (rowCellData) => {
                      const isDisabled = !rowCellData;

                      const onClick = (e: Event) => {
                          allTrackers.dispatchTrackEvent(
                              "Button",
                              "click",
                              "Website Keywords Pages/url count drilldown",
                          );
                      };

                      return (
                          <DrilldownWrapper>
                              <StyledEnrichButton
                                  height={24}
                                  onClick={isDisabled ? undefined : onClick}
                                  iconName="chev-down"
                                  isDisabled={isDisabled}
                              >
                                  <StyledEnrichButtonNumber isDisabled={isDisabled}>
                                      {rowCellData}
                                  </StyledEnrichButtonNumber>
                              </StyledEnrichButton>
                          </DrilldownWrapper>
                      );
                  },
                  sorter: true,
                  width: 95,
              }
            : null,

        {
            dataIndex: "siteSerpFeatures",
            title: translate("serp.column.name"),
            tooltipTitle: translate("serp.column.name"),
            tooltipSubtitle: translate("website.keywords.2.0.table.serp.column.tooltip"),
            width: compare ? 190 : 120,
            align: "left",
            render: (rowCellData, row) => {
                const props = {
                    value: rowCellData,
                    row: {
                        ...row,
                        SearchTerm: row.keyword,
                        SerpScrapeDate: row.serpScrapeDate,
                        SiteSerpFeatures: rowCellData,
                    },
                    tableOptions: {},
                };

                return compare ? <SerpTableCellCompare {...props} /> : <SerpTableCellSingle {...props} />;
            },
        },
    ].filter(Boolean);

    const sortedColumns = savedStateColumns ? filterAndSortColumnsForTable(columns, savedStateColumns) : columns;

    return [...fixedColumns, ...sortedColumns].map((col) =>
        findAndSetColumnSortOrder(col, sortConfig.sort, sortConfig.asc),
    );
};
